import * as React from 'react';

import { useNativeDataContext } from 'bundles/ent-website/components/NativeDataContext';
import type { NativeDataKey } from 'bundles/ent-website/components/NativeDataContext';
import MissingDataSourcesError from 'bundles/ent-website/contentful/core/MissingDataSourcesError';
import ShortCodeError from 'bundles/ent-website/contentful/core/ShortCodeError';
import { extractRequiredDataSourceNames, renderTemplate } from 'bundles/ent-website/utils/shortTags';

export class MissingDataSourceException extends Error {
  public missingDataSources: string[];

  constructor(msg: string, missingDataSources: string[]) {
    super(msg);
    this.missingDataSources = missingDataSources;
  }
}

export class DataSourceLoadingException extends Error {}

export function useDataConsumer() {
  const { isDataSourceLoading, isDataSourceExists, getNativeData } = useNativeDataContext();

  const replaceDataTags = React.useCallback(
    (tmpl?: string | null) => {
      if (!tmpl) {
        return tmpl;
      }

      const requiredDataSourceNames = extractRequiredDataSourceNames(tmpl) as NativeDataKey[];

      const missingDataSources = requiredDataSourceNames.filter((ds) => !isDataSourceExists(ds));

      if (missingDataSources.length > 0) {
        const err = new MissingDataSourceException('Missing data sources', missingDataSources);
        throw err;
      }

      if (requiredDataSourceNames.length === 0) {
        return tmpl;
      }

      if (isDataSourceLoading()) {
        throw new DataSourceLoadingException('Data sources are loading');
      }

      const context = requiredDataSourceNames.reduce((acc, ds) => {
        acc[ds] = getNativeData(ds);
        return acc;
      }, {} as Record<NativeDataKey, any>);

      const result = renderTemplate(tmpl, context);

      return result;
    },
    [getNativeData, isDataSourceExists, isDataSourceLoading]
  );

  return { replaceDataTags };
}

type Props = {
  template?: string | null;
  children: (props: { result?: string | null; isLoading?: boolean }) => JSX.Element | null;
};

export function DataConsumerWrapper({ template, children }: Props) {
  const { replaceDataTags } = useDataConsumer();

  if (!template) {
    return children({ result: template, isLoading: false });
  }

  try {
    const result = replaceDataTags(template) ?? null;
    return children({ result, isLoading: false });
  } catch (e) {
    if (e instanceof DataSourceLoadingException) {
      return children({ result: null, isLoading: true });
    } else if (e instanceof MissingDataSourceException) {
      return <MissingDataSourcesError missingDataSources={e.missingDataSources} />;
    } else {
      return <ShortCodeError error={String(e)} />;
    }
  }
}

export default DataConsumerWrapper;
